import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Download, Upload, Archive, RotateCcw, Shield, HardDrive, AlertTriangle, Trash2, Cloud, CloudUpload, Link as LinkIcon } from 'lucide-react';

const AdminTools = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('dry_run');

  // Backup & Restore states
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);

  // Archive states
  const [archivedFYs, setArchivedFYs] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [selectedFY, setSelectedFY] = useState('');

  // Image storage migration states
  const [imgStorage, setImgStorage] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      loadBackupInfo();
      loadArchivedFYs();
      loadImgStorage();
    }
  }, [user]);

  const loadBackupInfo = async () => {
    try {
      const res = await api.get('/admin/backup/info');
      setBackupInfo(res.data);
    } catch (err) { /* ignore */ }
  };

  const loadArchivedFYs = async () => {
    try {
      const res = await api.get('/admin/archived-fys');
      setArchivedFYs(res.data);
    } catch (err) { /* ignore */ }
  };

  const loadImgStorage = async () => {
    try {
      const res = await api.get('/admin/image-storage/status');
      setImgStorage(res.data);
    } catch (err) { /* ignore */ }
  };

  const runImageMigration = async () => {
    setMigrating(true); setMigrateResult(null);
    try {
      const res = await api.post('/admin/image-storage/migrate');
      setMigrateResult(res.data);
      toast.success(res.data.message);
      await loadImgStorage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image migration failed');
    } finally { setMigrating(false); }
  };

  const downloadBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get('/admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      link.setAttribute('download', `garment360_backup_${now.toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully! Save it to your pendrive for safekeeping.');
    } catch (err) {
      toast.error('Backup download failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFile = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.zip')) {
      setRestoreFile(file);
      setRestoreConfirm(true);
    } else {
      toast.error('Please select a valid .zip backup file');
    }
  };

  const executeRestore = async () => {
    if (!restoreFile) return;
    setRestoreLoading(true);
    setRestoreResult(null);
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      const res = await api.post('/admin/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRestoreResult(res.data);
      toast.success('Database restored successfully!');
      loadBackupInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreLoading(false);
      setRestoreConfirm(false);
      setRestoreFile(null);
    }
  };

  // Generate FY options (last 5 years)
  const getFYOptions = () => {
    const now = new Date();
    const currentYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const options = [];
    for (let i = 1; i <= 5; i++) {
      const startYear = currentYear - i;
      const endYear = startYear + 1;
      const label = `FY ${String(startYear).slice(2)}-${String(endYear).slice(2)}`;
      const start = `${startYear}-04-01`;
      const end = `${endYear}-03-31`;
      const alreadyArchived = archivedFYs.some(a => a.fy_label === label);
      options.push({ label, start, end, alreadyArchived });
    }
    return options;
  };

  const archiveFY = async (fy) => {
    setArchiveLoading(true);
    try {
      const res = await api.post(`/admin/archive-fy?fy_start=${fy.start}&fy_end=${fy.end}&fy_label=${encodeURIComponent(fy.label)}`);
      toast.success(`${fy.label} archived: ${res.data.total_archived} records`);
      setArchiveDialog(false);
      loadArchivedFYs();
      loadBackupInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    } finally {
      setArchiveLoading(false);
    }
  };

  const unarchiveFY = async (fyLabel) => {
    if (!window.confirm(`Restore ${fyLabel} back to active data?`)) return;
    try {
      const res = await api.post(`/admin/unarchive-fy?fy_label=${encodeURIComponent(fyLabel)}`);
      toast.success(`${fyLabel} restored: ${res.data.total_restored} records`);
      loadArchivedFYs();
      loadBackupInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unarchive failed');
    }
  };

  const purgeFY = async (fy) => {
    const purgeOos = window.confirm(
      `PERMANENTLY DELETE ${fy.fy_label}?\n\n` +
      `This will erase ALL archived data for this FY (cannot be undone).\n\n` +
      `Click OK to ALSO delete catalogue designs from this FY that are out of stock.\n` +
      `Click Cancel to keep catalogue designs (delete only transaction data).`
    );
    const finalConfirm = window.prompt(
      `To confirm permanent deletion of ${fy.fy_label}, type DELETE below:`
    );
    if (finalConfirm !== 'DELETE') { toast.error('Deletion cancelled'); return; }
    try {
      const params = new URLSearchParams({
        fy_label: fy.fy_label,
        fy_start: fy.fy_start,
        fy_end: fy.fy_end,
        purge_oos_designs: purgeOos ? 'true' : 'false',
      });
      const res = await api.post(`/admin/purge-fy?${params.toString()}`);
      const designsMsg = res.data.purged_designs > 0 ? ` + ${res.data.purged_designs} OOS designs` : '';
      toast.success(`${fy.fy_label} permanently deleted: ${res.data.total_deleted} records${designsMsg}`);
      loadArchivedFYs();
      loadBackupInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <div className="p-8 text-center text-red-600 font-semibold" style={{ fontFamily: 'Nunito' }}>
        Access denied. Admin only.
      </div>
    );
  }

  const runRecalibration = async () => {
    const isDry = mode === 'dry_run';
    if (!isDry) {
      const confirmed = window.confirm(
        'This will OVERWRITE stock values for ALL products based on active transactions.\n\nAre you sure? This action cannot be undone.'
      );
      if (!confirmed) return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post(`/admin/recalibrate-stock?dry_run=${isDry}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Recalibration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" style={{ fontFamily: 'Nunito' }}>
      <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-tools-title">Admin Tools</h1>
      <p className="text-gray-500 mb-8">System maintenance utilities for data integrity.</p>

      {/* Stock Recalibration Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="recalibration-card">
        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-bold text-gray-800">Stock Recalibration</h2>
          <p className="text-sm text-gray-500 mt-1">
            Rebuilds <code className="bg-gray-200 px-1 rounded text-xs">current_stock</code>,{' '}
            <code className="bg-gray-200 px-1 rounded text-xs">defect_stock</code>, and{' '}
            <code className="bg-gray-200 px-1 rounded text-xs">reserved_stock</code> for every product by replaying all active transactions (PE, SE, SR, PR, SO).
          </p>
        </div>

        <div className="px-6 py-5">
          {/* Mode selector */}
          <div className="flex items-center gap-4 mb-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="dry_run"
                checked={mode === 'dry_run'}
                onChange={() => setMode('dry_run')}
                className="accent-blue-600"
                data-testid="mode-dry-run"
              />
              <span className="text-sm font-medium text-gray-700">Preview (Dry Run)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="apply"
                checked={mode === 'apply'}
                onChange={() => setMode('apply')}
                className="accent-red-600"
                data-testid="mode-apply"
              />
              <span className="text-sm font-medium text-red-700">Apply Changes</span>
            </label>
          </div>

          <button
            onClick={runRecalibration}
            disabled={loading}
            className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              mode === 'apply'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            } disabled:opacity-50`}
            data-testid="run-recalibration-btn"
          >
            {loading ? 'Processing...' : mode === 'apply' ? 'Recalibrate & Save' : 'Run Preview'}
          </button>

          {error && <p className="mt-4 text-red-600 text-sm font-medium" data-testid="recalibration-error">{error}</p>}

          {/* Results */}
          {result && (
            <div className="mt-6" data-testid="recalibration-result">
              <div className="flex items-center gap-3 mb-4">
                {result.dry_run ? (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">DRY RUN</span>
                ) : (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">APPLIED</span>
                )}
                <span className="text-sm text-gray-600">
                  Scanned <strong>{result.total_products_scanned}</strong> products
                </span>
              </div>

              {result.products_changed === 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm font-medium" data-testid="no-changes">
                  All stock values are already correct. No changes needed.
                </div>
              ) : (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-amber-800 text-sm font-medium">
                      {result.products_changed} product(s) with {result.skus_changed} SKU discrepancies
                      {result.dry_run ? ' (preview only, nothing saved)' : ' — corrections applied'}.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100 text-left">
                          <th className="px-4 py-2.5 font-semibold text-gray-700">Design</th>
                          <th className="px-4 py-2.5 font-semibold text-gray-700">Size</th>
                          <th className="px-4 py-2.5 font-semibold text-gray-700">Color</th>
                          <th className="px-4 py-2.5 font-semibold text-gray-700 text-right">Before</th>
                          <th className="px-4 py-2.5 font-semibold text-gray-700 text-center">→</th>
                          <th className="px-4 py-2.5 font-semibold text-gray-700 text-right">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.changes.map((product) =>
                          product.sku_changes.map((sku, idx) => (
                            <tr key={`${product.product_id}-${idx}`} className="border-t border-gray-100 hover:bg-gray-50">
                              {idx === 0 && (
                                <td className="px-4 py-2.5 font-bold text-gray-900" rowSpan={product.sku_changes.length}>
                                  {product.design_code}
                                </td>
                              )}
                              <td className="px-4 py-2.5 text-gray-700">{sku.size}</td>
                              <td className="px-4 py-2.5 text-gray-700">{sku.color}</td>
                              <td className="px-4 py-2.5 text-right font-mono text-red-600">
                                <StockBadge label="CS" value={sku.before.current_stock} />
                                <StockBadge label="DS" value={sku.before.defect_stock} />
                                <StockBadge label="RS" value={sku.before.reserved_stock} />
                              </td>
                              <td className="px-4 py-2.5 text-center text-gray-400 text-lg">→</td>
                              <td className="px-4 py-2.5 text-right font-mono text-green-700">
                                <StockBadge label="CS" value={sku.after.current_stock} />
                                <StockBadge label="DS" value={sku.after.defect_stock} />
                                <StockBadge label="RS" value={sku.after.reserved_stock} />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">CS = Current Stock, DS = Defect Stock, RS = Reserved Stock</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Backup & Restore Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6" data-testid="backup-card">
        <div className="px-6 py-5 border-b border-gray-100 bg-blue-50">
          <div className="flex items-center gap-3">
            <HardDrive className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Backup & Restore</h2>
              <p className="text-sm text-gray-500 mt-1">Download a complete backup of all your data. Restore from a backup file if needed.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          {/* Backup Info */}
          {backupInfo && (
            <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Database Summary</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="text-center p-2 bg-white rounded border"><p className="font-bold text-lg text-blue-600">{backupInfo.total_records}</p><p className="text-gray-500">Total Records</p></div>
                <div className="text-center p-2 bg-white rounded border"><p className="font-bold text-lg text-emerald-600">{Object.keys(backupInfo.collections).length}</p><p className="text-gray-500">Collections</p></div>
                <div className="text-center p-2 bg-white rounded border"><p className="font-bold text-lg text-amber-600">{Object.keys(backupInfo.archived_collections).length}</p><p className="text-gray-500">Archived</p></div>
                <div className="text-center p-2 bg-white rounded border"><p className="font-bold text-lg text-gray-600">{backupInfo.collections.products || 0}</p><p className="text-gray-500">Products</p></div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {/* Download Backup */}
            <Button
              onClick={downloadBackup}
              disabled={backupLoading}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="download-backup-btn"
            >
              <Download className="w-4 h-4" />
              {backupLoading ? 'Preparing...' : 'Download Full Backup'}
            </Button>

            {/* Restore from Backup */}
            <label className="inline-flex">
              <input type="file" accept=".zip" onChange={handleRestoreFile} className="hidden" data-testid="restore-file-input" />
              <Button variant="outline" className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50 cursor-pointer" asChild>
                <span>
                  <Upload className="w-4 h-4" />
                  Restore from Backup
                </span>
              </Button>
            </label>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            <Shield className="w-3 h-3 inline mr-1" />
            Backup includes all data: customers, vendors, products, transactions, stock, users, and settings. Save the ZIP to your pendrive for safekeeping.
          </p>

          {/* Restore Result */}
          {restoreResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="restore-result">
              <p className="text-green-800 font-medium text-sm">Restored successfully from backup dated {restoreResult.backup_date?.slice(0,10)}</p>
              <p className="text-green-700 text-xs mt-1">{restoreResult.total_records} total records across {Object.keys(restoreResult.collections_restored).length} collections</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Storage Migration Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6" data-testid="image-storage-card">
        <div className="px-6 py-5 border-b border-gray-100 bg-indigo-50">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-indigo-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Image Storage Migration</h2>
              <p className="text-sm text-gray-500 mt-1">
                Move product images from the temporary container disk to permanent cloud storage so they survive every redeploy. Safe to run any time.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          {imgStorage && (
            <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="image-storage-stats">
              <div className="text-center p-3 bg-gray-50 rounded border border-gray-200">
                <p className="font-bold text-lg text-indigo-600">{imgStorage.disk_file_count}</p>
                <p className="text-gray-500">On Container Disk</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded border border-gray-200">
                <p className="font-bold text-lg text-emerald-600">{Math.round((imgStorage.disk_bytes || 0) / 1048576)} MB</p>
                <p className="text-gray-500">Disk Footprint</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded border border-gray-200">
                <p className="font-bold text-lg text-blue-600">{imgStorage.catalogue_image_refs}</p>
                <p className="text-gray-500">Catalogue Refs</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded border border-gray-200">
                <p className={`font-bold text-lg ${imgStorage.cloud_ready ? 'text-emerald-600' : 'text-red-600'}`}>
                  {imgStorage.cloud_ready ? 'Ready' : 'Offline'}
                </p>
                <p className="text-gray-500">Cloud Status</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={runImageMigration}
              disabled={migrating || !imgStorage?.cloud_ready || (imgStorage?.disk_file_count || 0) === 0}
              className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              data-testid="migrate-images-btn"
            >
              <CloudUpload className="w-4 h-4" />
              {migrating ? 'Migrating…' : `Migrate ${imgStorage?.disk_file_count || 0} Image${(imgStorage?.disk_file_count || 0) === 1 ? '' : 's'} to Cloud`}
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            <Shield className="w-3 h-3 inline mr-1" />
            Already-cloud images are simply overwritten with the same bytes — no data is lost. Run this once after any restore or large image import.
          </p>

          {migrateResult && (
            <div
              className={`mt-4 p-4 rounded-lg border ${migrateResult.failed ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}
              data-testid="migrate-result"
            >
              <p className={`font-medium text-sm ${migrateResult.failed ? 'text-amber-800' : 'text-green-800'}`}>
                {migrateResult.message}
              </p>
              {migrateResult.failed > 0 && migrateResult.failed_files?.length > 0 && (
                <p className="text-amber-700 text-xs mt-1 font-mono">
                  Failed: {migrateResult.failed_files.join(', ')}{migrateResult.failed > migrateResult.failed_files.length ? '…' : ''}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Colour Rename Tool */}
      <ColourRenameTool />

      {/* Retroactive SE↔SO Link Tool */}
      <RetroactiveLinkTool />
      <ReconcileDispatchTool />

      {/* FY Archive Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6" data-testid="archive-card">
        <div className="px-6 py-5 border-b border-gray-100 bg-amber-50">
          <div className="flex items-center gap-3">
            <Archive className="w-5 h-5 text-amber-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Financial Year Archive</h2>
              <p className="text-sm text-gray-500 mt-1">Archive old financial year data to keep your active database lean. Archived data can be restored anytime.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <Button
            onClick={() => setArchiveDialog(true)}
            className="bg-amber-600 hover:bg-amber-700 gap-2 mb-4"
            data-testid="archive-fy-btn"
          >
            <Archive className="w-4 h-4" />
            Archive a Financial Year
          </Button>

          {/* Archived FYs List */}
          {archivedFYs.length > 0 && (
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-700 mb-3">Archived Financial Years</p>
              <div className="space-y-2">
                {archivedFYs.map((fy, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <span className="font-bold text-gray-800">{fy.fy_label}</span>
                      <span className="text-xs text-gray-500 ml-2">({fy.fy_start} to {fy.fy_end})</span>
                      <span className="text-xs text-gray-400 ml-2">Archived {new Date(fy.timestamp).toLocaleDateString('en-IN')}</span>
                      {fy.archived_counts && (
                        <span className="text-xs text-gray-500 ml-2">
                          ({Object.values(fy.archived_counts).reduce((s, v) => s + v, 0)} records)
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                        onClick={() => unarchiveFY(fy.fy_label)}
                        data-testid={`unarchive-${fy.fy_label}`}
                      >
                        <RotateCcw className="w-3 h-3" />
                        Restore
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => purgeFY(fy)}
                        data-testid={`purge-${fy.fy_label}`}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete Permanently
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {archivedFYs.length === 0 && (
            <p className="text-xs text-gray-400 mt-2">No financial years archived yet.</p>
          )}
        </div>
      </div>

      {/* Archive FY Dialog */}
      <Dialog open={archiveDialog} onOpenChange={setArchiveDialog}>
        <DialogContent className="max-w-md z-50">
          <DialogHeader>
            <DialogTitle>Archive Financial Year</DialogTitle>
            <DialogDescription>Select a financial year to archive. Transaction data (SO, SE, SR, PO, PE, PR) will be moved to archive storage.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {getFYOptions().map(fy => (
              <div key={fy.label} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <div>
                  <span className="font-medium text-gray-800">{fy.label}</span>
                  <span className="text-xs text-gray-500 ml-2">({fy.start} to {fy.end})</span>
                </div>
                {fy.alreadyArchived ? (
                  <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-50 rounded">Archived</span>
                ) : (
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-xs"
                    disabled={archiveLoading}
                    onClick={() => archiveFY(fy)}
                    data-testid={`archive-btn-${fy.label}`}
                  >
                    {archiveLoading ? 'Archiving...' : 'Archive'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore Confirm Dialog */}
      <Dialog open={restoreConfirm} onOpenChange={(v) => { if (!v) { setRestoreConfirm(false); setRestoreFile(null); } }}>
        <DialogContent className="max-w-md z-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Confirm Restore
            </DialogTitle>
            <DialogDescription>
              This will <strong>replace ALL current data</strong> with the backup file. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            {restoreFile && (
              <p className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded-lg border">
                File: <strong>{restoreFile.name}</strong> ({(restoreFile.size / 1024).toFixed(0)} KB)
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setRestoreConfirm(false); setRestoreFile(null); }}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={executeRestore}
                disabled={restoreLoading}
                data-testid="confirm-restore-btn"
              >
                {restoreLoading ? 'Restoring...' : 'Yes, Restore Data'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const RetroactiveLinkTool = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runLink = async (dryRun) => {
    if (!dryRun && !window.confirm('This will link all unlinked SE items to matching pending SOs and update dispatch quantities. Continue?')) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/sales-entries/retroactive-link', { dry_run: dryRun });
      setResult(res.data);
      if (!dryRun && res.data.links_found > 0) toast.success(`Linked ${res.data.links_found} SE variant(s) to SOs`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6" data-testid="retroactive-link-card">
      <div className="px-6 py-5 border-b border-gray-100 bg-indigo-50">
        <div className="flex items-center gap-3">
          <LinkIcon className="w-5 h-5 text-indigo-600" />
          <div>
            <h2 className="text-lg font-bold text-gray-800">Retroactive SE↔SO Link</h2>
            <p className="text-sm text-gray-500 mt-1">
              Scan all Sales Entries and auto-link unlinked items to matching pending Sales Orders. Updates dispatch quantities.
            </p>
          </div>
        </div>
      </div>
      <div className="px-6 py-5">
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            onClick={() => runLink(true)} disabled={loading} data-testid="retroactive-preview-btn">
            {loading ? 'Scanning...' : 'Preview (Dry Run)'}
          </Button>
          <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700"
            onClick={() => runLink(false)} disabled={loading} data-testid="retroactive-apply-btn">
            {loading ? 'Linking...' : 'Apply Link'}
          </Button>
        </div>
        {result && (
          <div className={`mt-4 p-4 rounded-lg border ${result.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`} data-testid="retroactive-result">
            <p className={`font-semibold text-sm ${result.dry_run ? 'text-blue-800' : 'text-green-800'}`}>{result.message}</p>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div className="p-2 rounded bg-white border text-center">
                <p className="font-bold text-lg text-indigo-700">{result.links_found}</p><p className="text-gray-500">Links Found</p>
              </div>
              <div className="p-2 rounded bg-white border text-center">
                <p className="font-bold text-lg text-blue-700">{result.ses_affected}</p><p className="text-gray-500">SEs Affected</p>
              </div>
              <div className="p-2 rounded bg-white border text-center">
                <p className="font-bold text-lg text-emerald-700">{result.sos_affected}</p><p className="text-gray-500">SOs Updated</p>
              </div>
            </div>
            {result.details?.length > 0 && (
              <div className="mt-3 max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-100"><th className="p-1.5 text-left">SE</th><th className="p-1.5 text-left">SO</th><th className="p-1.5 text-left">Design</th><th className="p-1.5">Colour/Size</th><th className="p-1.5 text-right">Qty</th></tr></thead>
                  <tbody>
                    {result.details.map((d, i) => (
                      <tr key={i} className="border-t"><td className="p-1.5 font-mono">{d.se}</td><td className="p-1.5 font-mono">{d.so}</td><td className="p-1.5 font-bold">{d.design}</td><td className="p-1.5 text-center">{d.color}/{d.size}</td><td className="p-1.5 text-right font-bold">{d.qty}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {result.dry_run && result.links_found > 0 && (
              <p className="text-xs text-blue-600 mt-2 font-medium">Click &ldquo;Apply Link&rdquo; to make these changes permanent.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ReconcileDispatchTool = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runReconcile = async (dryRun) => {
    if (!dryRun && !window.confirm('This will recalculate qty_dispatched on all SOs from linked SE data. Continue?')) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/sales-entries/reconcile-dispatch', { dry_run: dryRun });
      setResult(res.data);
      if (!dryRun && res.data.fixes_needed > 0) toast.success(`Fixed ${res.data.fixes_needed} variant(s)!`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6" data-testid="reconcile-dispatch-card">
      <div className="px-6 py-5 border-b border-gray-100 bg-teal-50">
        <div className="flex items-center gap-3">
          <RotateCcw className="w-5 h-5 text-teal-600" />
          <div>
            <h2 className="text-lg font-bold text-gray-800">Reconcile Dispatch Quantities</h2>
            <p className="text-sm text-gray-500 mt-1">
              Recalculate SO dispatch quantities from all linked SE data. Fixes mismatches where SEs are linked but SO dispatch count is wrong.
            </p>
          </div>
        </div>
      </div>
      <div className="px-6 py-5">
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2 border-teal-300 text-teal-700 hover:bg-teal-50"
            onClick={() => runReconcile(true)} disabled={loading} data-testid="reconcile-preview-btn">
            {loading ? 'Scanning...' : 'Preview (Dry Run)'}
          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700"
            onClick={() => runReconcile(false)} disabled={loading} data-testid="reconcile-apply-btn">
            {loading ? 'Fixing...' : 'Apply Fix'}
          </Button>
        </div>
        {result && (
          <div className={`mt-4 p-4 rounded-lg border ${result.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`} data-testid="reconcile-result">
            <p className={`font-semibold text-sm ${result.dry_run ? 'text-blue-800' : 'text-green-800'}`}>{result.message}</p>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div className="p-2 rounded bg-white border text-center">
                <p className="font-bold text-lg text-teal-700">{result.fixes_needed}</p><p className="text-gray-500">Fixes Needed</p>
              </div>
              <div className="p-2 rounded bg-white border text-center">
                <p className="font-bold text-lg text-blue-700">{result.sos_affected}</p><p className="text-gray-500">SOs Affected</p>
              </div>
              <div className="p-2 rounded bg-white border text-center">
                <p className="font-bold text-lg text-amber-700">{result.total_qty_correction}</p><p className="text-gray-500">Qty Correction</p>
              </div>
            </div>
            {result.details?.length > 0 && (
              <div className="mt-3 max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-100"><th className="p-1.5 text-left">SO</th><th className="p-1.5 text-left">Design</th><th className="p-1.5">Colour/Size</th><th className="p-1.5 text-right">Was</th><th className="p-1.5 text-right">Should Be</th><th className="p-1.5 text-right">Diff</th></tr></thead>
                  <tbody>
                    {result.details.map((d, i) => (
                      <tr key={i} className="border-t"><td className="p-1.5 font-mono">{d.so}</td><td className="p-1.5 font-bold">{d.design}</td><td className="p-1.5 text-center">{d.color}/{d.size}</td><td className="p-1.5 text-right text-red-600">{d.was}</td><td className="p-1.5 text-right text-green-600">{d.should_be}</td><td className="p-1.5 text-right font-bold">{d.diff > 0 ? `+${d.diff}` : d.diff}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {result.dry_run && result.fixes_needed > 0 && (
              <p className="text-xs text-blue-600 mt-2 font-medium">Click &ldquo;Apply Fix&rdquo; to correct these dispatch quantities.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


const StockBadge = ({ label, value }) => (
  <span className="inline-block mr-2">
    <span className="text-[10px] text-gray-400">{label}:</span>
    <span className="ml-0.5">{value}</span>
  </span>
);

const ColourRenameTool = () => {
  const [designCode, setDesignCode] = useState('');
  const [oldColour, setOldColour] = useState('');
  const [newColour, setNewColour] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [fetchingColors, setFetchingColors] = useState(false);

  // Auto-fetch colours when design code changes
  useEffect(() => {
    const code = designCode.trim();
    if (!code) { setAvailableColors([]); setOldColour(''); return; }
    const timer = setTimeout(async () => {
      setFetchingColors(true);
      try {
        const res = await api.get(`/products/search/${encodeURIComponent(code)}`);
        const prodList = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
        const prod = prodList[0];
        if (prod) {
          const colors = new Set();
          const ic = prod.image_colors;
          if (Array.isArray(ic)) ic.forEach(c => colors.add(c));
          else if (ic && typeof ic === 'object') Object.values(ic).forEach(c => { if (c) colors.add(c); });
          (prod.skus || []).forEach(s => { if (s.color) colors.add(s.color); });
          setAvailableColors([...colors].filter(Boolean).sort());
        } else { setAvailableColors([]); }
      } catch { setAvailableColors([]); }
      finally { setFetchingColors(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [designCode]);

  const runRename = async (dryRun) => {
    if (!designCode.trim() || !oldColour.trim() || !newColour.trim()) {
      toast.error('Fill all three fields');
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(
        `This will permanently rename "${oldColour}" → "${newColour}" for design ${designCode} across ALL Sales Orders, Sales Entries, Purchase Entries, Purchase Orders, Returns, and Catalogue.\n\nContinue?`
      );
      if (!ok) return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/admin/rename-colour', {
        design_code: designCode.trim(),
        old_colour: oldColour.trim(),
        new_colour: newColour.trim(),
        dry_run: dryRun,
      });
      setResult(res.data);
      if (!dryRun && res.data.total_affected > 0) {
        toast.success(`Renamed "${oldColour}" → "${newColour}" in ${res.data.total_affected} document(s)`);
        // Refresh available colours — replace old with new in dropdown
        setAvailableColors(prev => prev.map(c => c === oldColour.trim() ? newColour.trim() : c).sort());
        setOldColour('');
        setNewColour('');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6" data-testid="colour-rename-card">
      <div className="px-6 py-5 border-b border-gray-100 bg-violet-50">
        <div className="flex items-center gap-3">
          <RotateCcw className="w-5 h-5 text-violet-600" />
          <div>
            <h2 className="text-lg font-bold text-gray-800">Bulk Colour Rename</h2>
            <p className="text-sm text-gray-500 mt-1">
              Rename a colour for a specific design across <strong>all</strong> transactions — SO, SE, PE, PO, SR, PR, and Catalogue.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Design Code</label>
            <input
              type="text"
              value={designCode}
              onChange={(e) => setDesignCode(e.target.value)}
              placeholder="e.g. 9525"
              className="w-full h-10 px-3 border-2 border-gray-300 rounded-lg text-sm font-semibold focus:border-violet-500 focus:outline-none"
              data-testid="rename-design-code"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Old Colour {fetchingColors && <span className="text-gray-400">(loading...)</span>}</label>
            {availableColors.length > 0 ? (
              <select
                value={oldColour}
                onChange={(e) => setOldColour(e.target.value)}
                className="w-full h-10 px-3 border-2 border-red-200 rounded-lg text-sm font-semibold bg-red-50/50 focus:border-red-500 focus:outline-none"
                data-testid="rename-old-colour"
              >
                <option value="">Select colour...</option>
                {availableColors.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input
                type="text"
                value={oldColour}
                onChange={(e) => setOldColour(e.target.value)}
                placeholder="e.g. Cream"
                className="w-full h-10 px-3 border-2 border-red-200 rounded-lg text-sm font-semibold bg-red-50/50 focus:border-red-500 focus:outline-none"
                data-testid="rename-old-colour"
              />
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">New Colour</label>
            <input
              type="text"
              value={newColour}
              onChange={(e) => setNewColour(e.target.value)}
              placeholder="e.g. Olive"
              className="w-full h-10 px-3 border-2 border-green-200 rounded-lg text-sm font-semibold bg-green-50/50 focus:border-green-500 focus:outline-none"
              data-testid="rename-new-colour"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50"
            onClick={() => runRename(true)}
            disabled={loading}
            data-testid="rename-preview-btn"
          >
            {loading ? 'Checking...' : 'Preview (Dry Run)'}
          </Button>
          <Button
            className="gap-2 bg-violet-600 hover:bg-violet-700"
            onClick={() => runRename(false)}
            disabled={loading}
            data-testid="rename-apply-btn"
          >
            {loading ? 'Renaming...' : 'Apply Rename'}
          </Button>
        </div>

        {result && (
          <div className={`mt-4 p-4 rounded-lg border ${result.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`} data-testid="rename-result">
            <p className={`font-semibold text-sm ${result.dry_run ? 'text-blue-800' : 'text-green-800'}`}>
              {result.message}
            </p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {Object.entries(result.affected_documents || {}).map(([coll, count]) => (
                <div key={coll} className={`p-2 rounded text-center ${count > 0 ? 'bg-white border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <p className={`font-bold text-base ${count > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{count}</p>
                  <p className="text-gray-500 capitalize">{coll.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
            {result.dry_run && result.total_affected > 0 && (
              <p className="text-xs text-blue-600 mt-2 font-medium">
                Click &ldquo;Apply Rename&rdquo; to make these changes permanent.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTools;
